import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Alert } from '@mui/material';
import Cookies from 'js-cookie';
import { useLazyQuery } from '@apollo/client';
import { styled } from '@mui/material/styles';
import TagManager from 'react-gtm-module';
import Subscribe from '../components/Subscribe';
import Spinner from '../components/Spinner';
import Page from '../components/Page';
import { GET_USER_STATUS } from '../graphql';
import getErrorCode from '../utils/getErrorCode';

const ContentStyle = styled('div')(({ theme }) => ({
  maxWidth: 480,
  margin: 'auto',
  display: 'flex',
  minHeight: '100vh',
  flexDirection: 'column',
  justifyContent: 'center',
  padding: theme.spacing(12, 0),
}));

const Home = () => {
  const navigate = useNavigate();
  const [showError, setShowError] = useState(false);
  const [checkingMsisdn, setCheckingMsisdn] = useState(true);

  const [getUserStatus, { data, loading, error }] = useLazyQuery(
    GET_USER_STATUS,
    {
      fetchPolicy: 'network-only',
    }
  );

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const click_id = urlParams.get('click_id') || '';
    const advertiser_id = urlParams.get('advertiser_id') || 'Solunet';

    sessionStorage.setItem('click_id', click_id);
    sessionStorage.setItem('advertiser_id', advertiser_id);

    let cancelled = false;

    const checkUserStatus = async () => {
      try {
        setCheckingMsisdn(true);

        // Call the backend service to retrieve the masked MSISDN.
        const apiBaseUrl =
          process.env.REACT_APP_API_URL || 'https://gameszetu.com';
        const baseUrl = apiBaseUrl
        .replace(/\/graphql\/?$/, '')
        .replace(/\/$/, '');

      const endpoint = `${baseUrl}/api/fetch-masked-msisdn`;

        const response = await fetch(
          endpoint,
          {
            method: 'GET',
            headers: {
              Accept: 'application/json',
            },
            credentials: 'include',
          }
        );

        if (!response.ok) {
          throw new Error(
            `MSISDN request failed: HTTP ${response.status}`
          );
        }

        const result = await response.json();
        const msisdn = result?.msisdn;

        if (!msisdn || typeof msisdn !== 'string') {
          throw new Error('MSISDN request failed: no MSISDN returned');
        }

        if (cancelled) return;

        console.log('MSISDN lookup successful');

        // Save the MSISDN after the backend request succeeds.
        localStorage.setItem('msisdn', msisdn);
        localStorage.setItem('msisdnHash', msisdn);
        Cookies.set('msisdn', msisdn);

        // Trigger GraphQL only after the MSISDN check passes.
        getUserStatus();
      } catch (err) {
        console.error('MSISDN lookup failed:', err);

        if (cancelled) return;

        Cookies.remove('msisdn');
        Cookies.remove('msisdnHash');
        localStorage.removeItem('msisdn');
        localStorage.removeItem('msisdnHash');

        navigate('/he-disable');
      } finally {
        if (!cancelled) {
          setCheckingMsisdn(false);
        }
      }
    };

    checkUserStatus();

    return () => {
      cancelled = true;
    };
  }, [navigate, getUserStatus]);

  useEffect(() => {
    if (data?.getUserStatus) {
      const { msisdn, responseCode } = data.getUserStatus;

      switch (responseCode) {
        case 200:
          localStorage.setItem('subscribed', 'true');

          TagManager.dataLayer({
            dataLayer: {
              event: 'userSubscribed',
              userId: msisdn,
            },
          });

          setTimeout(() => {
            navigate('/app/games');
          }, 1000);
          break;

        case 100:
        case 400:
        case 600:
        case 700:
        case 800:
          localStorage.setItem('subscribed', '');
          navigate('/subscribe?m=unable');
          break;

        case 500:
          localStorage.setItem('subscribed', '');
          navigate('/not-subscribe');
          break;

        default:
          Cookies.set('msisdn', '');
          localStorage.setItem('msisdn', '');
          navigate('/auth');
          break;
      }
    } else if (error) {
      setTimeout(() => setShowError(true), 4000);
    }
  }, [data, error, navigate]);

  return (
    <Page title="Home | GamesZetu">
      <Container maxWidth="xl">
        <ContentStyle>
          {(checkingMsisdn || loading) && (
            <>
              <Spinner />
              <Alert severity="info">
                Just a second, adventure mode awaits....
              </Alert>
            </>
          )}

          {!checkingMsisdn && !loading && error && showError && (
            <>
              {error.graphQLErrors && getErrorCode(error) === 404 ? (
                <Subscribe code={getErrorCode(error)} />
              ) : (
                <Alert severity="error">
                  Opps! Glitch alert—refresh to game on!.
                </Alert>
              )}
            </>
          )}
        </ContentStyle>
      </Container>
    </Page>
  );
};

export default Home;